import { Bell, Dumbbell } from "lucide-react";
import type { ReactNode } from "react";

import { Monogram, Section, SectionHead } from "@/features/portal/components/portal-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** Times for the drawing in step two. They illustrate a calendar; they are not anyone's open times. */
const SAMPLE_TIMES = ["07:00", "07:30", "09:00", "12:00", "17:30", "18:30"];
const SAMPLE_PICK = 4;

const mini =
  "w-[min(270px,100%)] rounded-[18px] bg-background p-3.5 shadow-[0_14px_30px_color-mix(in_oklch,var(--primary)_14%,transparent)]";
const badge = "grid size-8 shrink-0 place-items-center rounded-full bg-[var(--site-wash)] text-[var(--site-ink)]";

const MiniRow = ({ icon, title, detail }: { icon: ReactNode; title: string; detail: string }) => (
  <div className={cn(mini, "flex items-center gap-2.5 font-medium")}>
    {icon}
    <div className="min-w-0">
      <span className="block truncate">{title}</span>
      <small className="block truncate text-xs font-normal text-muted-foreground">{detail}</small>
    </div>
  </div>
);

/** Numbered, because the order is the point: find somebody, book a time, train. */
const steps = () => [
  {
    id: "find",
    title: m.portal_step_find_title(),
    body: m.portal_step_find_body(),
    art: (
      <MiniRow
        icon={<Monogram name={m.portal_step_find_card_title()} className="size-8 rounded-full text-[11px]" />}
        title={m.portal_step_find_card_title()}
        detail={m.portal_step_find_card_detail()}
      />
    ),
  },
  {
    id: "book",
    title: m.portal_step_book_title(),
    body: m.portal_step_book_body(),
    art: (
      <div className={cn(mini, "grid grid-cols-3 gap-1.5")}>
        {SAMPLE_TIMES.map((time, index) => (
          <span
            key={time}
            className={cn(
              "grid h-8 place-items-center rounded-[10px] bg-muted text-xs font-semibold tabular-nums",
              index === SAMPLE_PICK && "bg-foreground text-background",
            )}
          >
            {time}
          </span>
        ))}
      </div>
    ),
  },
  {
    id: "train",
    title: m.portal_step_train_title(),
    body: m.portal_step_train_body(),
    art: (
      <div className="grid w-[min(270px,100%)] gap-2">
        <MiniRow
          icon={
            <span className={badge}>
              <Dumbbell aria-hidden className="size-4" />
            </span>
          }
          title={m.portal_step_train_card_title()}
          detail={m.portal_step_train_card_detail()}
        />
        <MiniRow
          icon={
            <span className={badge}>
              <Bell aria-hidden className="size-4" />
            </span>
          }
          title={m.portal_step_remind_card_title()}
          detail={m.portal_step_remind_card_detail()}
        />
      </div>
    ),
  },
];

/** How a person starts: find somebody, book a time, train. */
export const PortalSteps = () => (
  <Section id="how">
    <SectionHead
      title={
        <>
          {m.portal_steps_title_lead()}
          <br />
          {m.portal_steps_title_tail()}
        </>
      }
      aside={m.portal_steps_aside()}
    />
    <ol className="grid gap-3 lg:grid-cols-3">
      {steps().map((step, index) => (
        <li
          key={step.id}
          className={cn(
            "flex min-h-[300px] flex-col rounded-[28px] p-6 lg:min-h-[360px]",
            index === 1 ? "bg-[var(--site-wash)]" : "bg-[var(--site-tint)]",
          )}
        >
          <span className="text-[13px] text-muted-foreground tabular-nums">{String(index + 1).padStart(2, "0")}</span>
          <div aria-hidden className="grid flex-1 place-items-center py-[18px]">
            {step.art}
          </div>
          <b className="text-3xl font-medium tracking-[-0.03em]">{step.title}</b>
          <p className="mt-1 text-muted-foreground">{step.body}</p>
        </li>
      ))}
    </ol>
  </Section>
);
